// src/components/media/FolderTree.tsx — the folder tree: buildings → Building photos and flats → Photos / Floorplans /
// EPC, plus staff folders. Collapsible; the open folder's path is expanded. Inside the grid's drag context every folder
// is a drop target: dropping selected files on it moves them there.
import { useMemo, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronRight, Building2, DoorOpen, Folder, FolderOpen, Images, LayoutGrid, FileBadge, Library } from 'lucide-react';
import type { FolderKind, MediaFolder } from '../../lib/media';

export const FOLDER_ICON: Record<FolderKind, typeof Folder> = {
  PROPERTY: Building2, PROPERTY_GALLERY: Images, UNIT: DoorOpen, UNIT_PHOTOS: Images, UNIT_FLOORPLANS: LayoutGrid, UNIT_EPC: FileBadge, CUSTOM: Folder,
};

/** The drop target id for a folder (the grid's drag end reads it back). */
export const folderDropId = (id: string) => `folder:${id}`;

function pathTo(roots: MediaFolder[], id: string | null): string[] {
  if (!id) return [];
  const walk = (list: MediaFolder[], trail: string[]): string[] | null => {
    for (const f of list) {
      if (f.id === id) return trail;
      const hit = f.children?.length ? walk(f.children, [...trail, f.id]) : null;
      if (hit) return hit;
    }
    return null;
  };
  return walk(roots, []) ?? [];
}

function Node({ f, depth, selectedId, open, toggle, onSelect, dropTargets }: {
  f: MediaFolder; depth: number; selectedId: string | null; open: Set<string>; toggle: (id: string) => void; onSelect: (id: string) => void; dropTargets: boolean;
}) {
  const kids = f.children ?? [];
  const expanded = open.has(f.id);
  const active = f.id === selectedId;
  const { setNodeRef, isOver } = useDroppable({ id: folderDropId(f.id), data: { folderId: f.id, name: f.name }, disabled: !dropTargets });
  const I = active && f.kind === 'CUSTOM' ? FolderOpen : FOLDER_ICON[f.kind] ?? Folder;
  return (
    <li>
      <div ref={setNodeRef} style={{ paddingLeft: 4 + depth * 14 }}
        className={`group flex h-8 items-center gap-1 rounded-md pr-2 text-[12.5px] transition ${isOver ? 'bg-bronze-50 ring-2 ring-bronze-500' : active ? 'bg-bronze-50 font-semibold text-bronze-700' : 'text-ink-700 hover:bg-ink-100'}`}>
        <button type="button" tabIndex={-1} aria-label={expanded ? `Collapse ${f.name}` : `Expand ${f.name}`} onClick={() => toggle(f.id)}
          className={`grid h-5 w-5 shrink-0 place-items-center rounded text-ink-400 hover:text-ink-700 ${kids.length ? '' : 'invisible'}`}>
          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        <button type="button" onClick={() => onSelect(f.id)} aria-current={active ? 'true' : undefined} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          <I className={`h-3.5 w-3.5 shrink-0 ${active ? 'text-bronze-600' : 'text-ink-400'}`} />
          <span className="truncate">{f.name}</span>
          {!!f.itemCount && <span className="ml-auto shrink-0 pl-1 text-[11px] font-normal tabular-nums text-ink-400">{f.itemCount}</span>}
        </button>
      </div>
      {expanded && kids.length > 0 && (
        <ul>{kids.map((c) => <Node key={c.id} f={c} depth={depth + 1} selectedId={selectedId} open={open} toggle={toggle} onSelect={onSelect} dropTargets={dropTargets} />)}</ul>
      )}
    </li>
  );
}

export function FolderTree({ roots, selectedId, onSelect, dropTargets = false, rootLabel = 'Media', filter = '' }: {
  roots: MediaFolder[]; selectedId: string | null; onSelect: (id: string | null) => void; dropTargets?: boolean; rootLabel?: string | null; filter?: string;
}) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(pathTo(roots, selectedId)));
  // Opening a folder from elsewhere (the grid, a breadcrumb) expands the tree down to it.
  const trail = useMemo(() => pathTo(roots, selectedId), [roots, selectedId]);
  const [opened, setOpened] = useState(trail);
  if (opened !== trail) { setOpened(trail); if (!trail.every((id) => open.has(id))) setOpen(new Set([...open, ...trail])); }
  const toggle = (id: string) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const q = filter.trim().toLowerCase();
  const shown = q ? roots.filter((r) => r.name.toLowerCase().includes(q)) : roots;
  return (
    <nav aria-label="Folders" className="text-ink-700">
      {rootLabel !== null && (
        <button type="button" onClick={() => onSelect(null)}
          className={`mb-0.5 flex h-8 w-full items-center gap-1.5 rounded-md px-2 text-[12.5px] transition ${selectedId === null ? 'bg-bronze-50 font-semibold text-bronze-700' : 'text-ink-700 hover:bg-ink-100'}`}>
          <Library className={`h-3.5 w-3.5 ${selectedId === null ? 'text-bronze-600' : 'text-ink-400'}`} />{rootLabel}
        </button>
      )}
      <ul>{shown.map((f) => <Node key={f.id} f={f} depth={0} selectedId={selectedId} open={open} toggle={toggle} onSelect={onSelect} dropTargets={dropTargets} />)}</ul>
      {q && !shown.length && <p className="px-2 py-3 text-[12px] text-ink-400">No building or folder called “{filter.trim()}”.</p>}
    </nav>
  );
}
